import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, IconButton, TextField, Button, Link as MuiLink } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import './Footer.css';
import { useNavigate, useLocation } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import Popper from '@mui/material/Popper';

const Footer: React.FC = () => {
  const [footerWord, setFooterWord] = React.useState('Your Health');
  const [footerVisible, setFooterVisible] = React.useState(true);
  useEffect(() => {
    const words = ['Your Health', 'Our Priority'];
    let currentIndex = 0;
    const interval = setInterval(() => {
      setFooterVisible(false);
      setTimeout(() => {
        currentIndex = (currentIndex + 1) % words.length;
        setFooterWord(words[currentIndex]);
        setFooterVisible(true);
      }, 800); // fade duration
    }, 2200 + 800); // visible + fade
    return () => clearInterval(interval);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const specialPaths = ['/home', '/partnerpage', '/partner-form', '/about-us'];
  const isSpecialPage = specialPaths.includes(location.pathname);
  const isMobile = useMediaQuery('(max-width:900px)');
  const [servicesOpen, setServicesOpen] = React.useState<null | HTMLElement>(null);

  // Navigation handlers
  const handleMenuClick = (label: string, path: string) => {
    setServicesOpen(null);
    navigate(path);
  };
  const handleServiceSubClick = (label: string, path: string) => {
    setServicesOpen(null);
    navigate(path);
  };

  // Remove conditional logic; always show the spacer above the footer
  // const showFooterSpacer = location.pathname !== '/home' && location.pathname !== '/partnerpage' && location.pathname !== '/about-us';

  return (
    <>
      <div style={{ height: 56, minHeight: 0, width: '100%' }} />
      <footer
        className={`tachafy-footer-root ${isSpecialPage ? 'special-colors' : 'normal-colors'}`}
      >
      {/* Newsletter Section */}
      <Box
        className="footer-newsletter"
      >
        <Box>
          <Typography
            variant="h6"
            className="footer-newsletter-title"
            sx={{
              fontSize: { xs: '1.15rem', sm: '1.35rem', md: '1.6rem' },
              fontWeight: 700,
            }}
          >
            Join our Newsletter
          </Typography>
          <Typography
            className="footer-newsletter-desc"
            sx={{
              fontSize: { xs: '0.98rem', sm: '1.08rem', md: '1.13rem' },
              fontWeight: 400,
            }}
          >
            Subscribe to receive all the latest news & updates.
          </Typography>
        </Box>
        <Box className="footer-newsletter-form">
          <TextField
            placeholder="Your e-mail adress"
            variant="outlined"
            size="small"
            className="footer-newsletter-input"
            InputProps={{
              style: {
                background: 'transparent',
                color: '#fff',
                borderRadius: 30,
                border: '1.5px solid #fff',
                paddingLeft: 16,
                fontSize: 16,
              },
            }}
            inputProps={{
              style: { color: '#fff' },
              placeholder: 'Your e-mail adress',
            }}
          />
          <Button
  variant="contained"
  className="footer-newsletter-btn"
  sx={{
    background: isSpecialPage ? '#d5006e' : '#FFFFFF',
    color: isSpecialPage ? '#fff' : '#131B5C',
    borderRadius: 30,
    fontWeight: 600,
    fontSize: 16,
    height: 44,
    px: 4,
    ml: 2,
    boxShadow: 'none',
    pointerEvents: 'none', 
    cursor: 'default', 
    '&:hover': { background: isSpecialPage ? '#b8005a' : '#f0f0f0' },
  }}
>
  Subscribe
</Button>

        </Box>
                </Box>

      {/* Main Footer Section */}
      <Box className="footer-main" sx={{
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: isMobile ? 2 : 0,
        width: '100%',
        pl: { xs: 0, sm: 4, md: 8, lg: 12 },
        color: '#fff',
      }}>
        <Box sx={{
          minHeight: 100,
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          height: { xs: 100, sm: 120, md: 160 },
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
          mb: 0,
          
        }}>
          <Typography
            className="footer-main-title"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '4rem', lg: '5rem' },
              fontWeight: 800,
              color: '#fff',
              opacity: footerVisible ? 1 : 0,
              transition: 'opacity 0.8s',
              width: 'auto',
              textAlign: 'left',
              lineHeight: 1.1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
            }}
          >
            {footerWord}
          </Typography>
          <Stack
  direction="row"
  spacing={2}
  className="footer-socials"
  sx={{ opacity: 0.64, mt: isMobile ? 2 : 0 }}
>
  <IconButton
    aria-label="Facebook"
    className="footer-social-btn"
    sx={{ p: 1.5 }}
    component="a"
    href="https://www.facebook.com/people/Tachafy/61581992396073/"
    target="_blank"
    rel="noopener noreferrer"
  >
    <FacebookIcon fontSize="large" sx={{ color: '#fff' }} />
  </IconButton>

  <IconButton
    aria-label="Instagram"
    className="footer-social-btn"
    sx={{ p: 1.5 }}
    component="a"
    href="https://www.instagram.com/tachafy.official/"
    target="_blank"
    rel="noopener noreferrer"
  >
    <InstagramIcon fontSize="large" sx={{ color: '#fff' }} />
  </IconButton>

  <IconButton
    aria-label="LinkedIn"
    className="footer-social-btn"
    sx={{ p: 1.5 }}
    component="a"
    href="https://www.linkedin.com/company/tachafy/"
    target="_blank"
    rel="noopener noreferrer"
  >
    <LinkedInIcon fontSize="large" sx={{ color: '#fff' }} />
  </IconButton>
</Stack>

        </Box>
        {!isMobile && (
          <Box className="footer-links" sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 4,
            mt: 2,
            width: 'auto',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            ml: 0,
          }}>
            <Typography className="footer-link" sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 400, cursor: 'pointer' }} onClick={() => handleMenuClick('Home', '/home')}>Home</Typography>
            <Typography className="footer-link" sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 400, cursor: 'pointer' }} onClick={() => handleMenuClick('Healix', '/healixhome')}>Healix</Typography>
            <Typography className="footer-link" sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 400, cursor: 'pointer' }} onClick={() => handleMenuClick('Tele-Consult', '/teleconsultation')}>Tele-Consult</Typography>
            <Typography className="footer-link" sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 400, cursor: 'pointer' }} onClick={() => handleMenuClick('E-Pharmacy', '/EPharmacy')}>E-Pharmacy</Typography>
            <Typography className="footer-link" sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 400, cursor: 'pointer' }} onClick={() => handleMenuClick('About', '/about-us')}>About</Typography>
            <Typography className="footer-link" sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 400, cursor: 'pointer' }} onClick={() => handleMenuClick('Blog', '/blogs')}>Blog</Typography>
            <Typography className="footer-link" sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 400, cursor: 'pointer' }} onClick={() => handleMenuClick('Contact', '/contact-us')}>Contact</Typography>
            <Typography className="footer-link" sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 400, cursor: 'pointer' }} onClick={() => handleMenuClick('For partner', '/partnerpage')}>For partner</Typography>
          </Box>
        )}
      </Box>

      {/* Bottom Bar */}
      <Box className="footer-bottom" sx={{ color: '#fff' }}>
        <Typography className="footer-copyright" sx={{ color: 'rgba(255,255,255,0.64)', fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
          © 2025 TACHAFY
        </Typography>
        <Box className="footer-policy-links">
          <Typography className="footer-policy-link" sx={{ color: 'rgba(255,255,255,0.64)', fontSize: { xs: '0.85rem', sm: '0.95rem' }, fontWeight: 400, mx: 1, cursor: 'pointer' }}>Terms</Typography>
          <Typography className="footer-policy-link" sx={{ color: 'rgba(255,255,255,0.64)', fontSize: { xs: '0.85rem', sm: '0.95rem' }, fontWeight: 400, mx: 1, cursor: 'pointer' }}>Privacy</Typography>
          <Typography className="footer-policy-link" sx={{ color: 'rgba(255,255,255,0.64)', fontSize: { xs: '0.85rem', sm: '0.95rem' }, fontWeight: 400, mx: 1, cursor: 'pointer' }}>Cookies</Typography>
        </Box>
      </Box>
        </footer>
    </>
  );
};

export default Footer;
